import { modalIds } from '@const/modals';
import {
    apiAuthService,
    apiUserService,
    apiKeyService,
    apiMerchantConfigService,
    MerchantConfig,
    SCOPE_FULL_ACCESS,
    scopesForLevel,
    describeScopes
} from '@api';
import { useLocales } from '@hooks/helpers/useLocales';
import { useUserContext } from '@hooks/context/useUserContext';
import useModalContext from '@hooks/context/useModalContext';
import type { Environment } from '@context/environment_context';
import {
    ETwoFAMethod,
    TEmail,
    TNewPhone,
    TPhone
} from '@xeppt/xeppt-sdk/types';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { useState } from 'react';

export const useSettingsModals = () => {
    const { requestErrorLocale, requestSuccessLocale } = useLocales();
    const { refetchUser, user } = useUserContext();
    const { onOpen, onClose, handleChangeLoading, changeModalData } = useModalContext();
    const [authenticatorCode, setAuthenticatorCode] = useState<string>();

    const { handleRequest: handleVerifyMFA } = useApiMutation({
        method: (data: { token: string; code: string }) => {
            return apiAuthService.confirmTwoFA(data);
        },
        onSuccess: (data) => {
            if (data) {
                setAuthenticatorCode(data);
            }
            refetchUser();
            onClose();
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleDisableMFARequest } = useApiMutation({
        method: (type: ETwoFAMethod) => {
            handleChangeLoading(true);
            return apiAuthService.disableTwoFA();
        },
        onSuccess: (data, request) => {
            if (data) {
                handleConfirmDisableMFA(request, data.token);
            } else {
                refetchUser();
                onClose();
            }
        },
        onFinish: () => handleChangeLoading(false),
        onError: requestErrorLocale
    });

    const { handleRequest: handleResetOTPRequest } = useApiMutation({
        method: (code: string) => {
            return apiAuthService.resetTwoFA(code);
        },
        onSuccess: () => {
            refetchUser();
            onClose();
        },
        onError: requestErrorLocale
    });

    const handleChangeAvatar = () => {
        changeModalData({ avatar: user?.profile?.avatar });
        onOpen({
            modalId: modalIds.SETTINGS_UPLOAD_IMAGE,
            onSubmit: (data: { avatar: string }) => {
                handleChangeLoading(true);
                apiUserService
                    .updateAvatar(data.avatar)
                    .then(() => {
                        refetchUser();
                        onClose();
                        requestSuccessLocale('update_avatar');
                    })
                    .catch(requestErrorLocale)
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleDeleteAvatar = () => {
        apiUserService
            .deleteAvatar()
            .then(() => {
                refetchUser();
                requestSuccessLocale('delete_avatar');
                onClose();
            })
            .catch(requestErrorLocale);
    };

    // Business-account logo upload. Opens the exact same crop/upload modal used for the
    // personal avatar (SETTINGS_UPLOAD_IMAGE) — same drag-and-drop, crop editor, and submit
    // flow — but sends the resulting image to the merchant-config logo endpoint instead of
    // the personal avatar endpoint. `isBusiness: true` in modalData switches the modal's
    // copy to "Add/Update company logo" and suppresses its "delete current photo" action
    // (that action is hardcoded to delete the personal avatar, not the merchant logo, so it
    // must stay hidden here until a dedicated delete-logo endpoint exists).
    const handleChangeLogo = (
        currentLogo: string | undefined,
        onSuccess: (config: MerchantConfig) => void,
        environment: Environment = 'live'
    ) => {
        changeModalData({ avatar: currentLogo, isBusiness: true });
        onOpen({
            modalId: modalIds.SETTINGS_UPLOAD_IMAGE,
            onSubmit: (data: { avatar: string }) => {
                handleChangeLoading(true);
                // Backend expects raw base64 (no "data:image/...;base64," prefix) — the
                // crop editor's canvas.toDataURL() always includes that prefix.
                const commaIndex = data.avatar.indexOf(',');
                const rawBase64 =
                    commaIndex === -1 ? data.avatar : data.avatar.slice(commaIndex + 1);
                apiMerchantConfigService
                    .uploadLogo(rawBase64, environment)
                    .then((updated) => {
                        onSuccess(updated);
                        onClose();
                        requestSuccessLocale('update_logo');
                    })
                    .catch(requestErrorLocale)
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleOpenChangePasswordModal = () => {
        onOpen({
            modalId: modalIds.USER_VERIFICATION_PHONE,
            onSubmit: (data: { secure_id: string; code: string }) => {
                onOpen({
                    modalId: modalIds.SETTINGS_CHANGE_PASSWORD,
                    onSubmit: ({
                        oldPassword,
                        newPassword
                    }: {
                        oldPassword: string;
                        newPassword: string;
                    }) => {
                        handleChangeLoading(true);
                        apiUserService
                            .changePassword(oldPassword, newPassword, {
                                id: data.secure_id,
                                code: data.code
                            })
                            .then(() => {
                                requestSuccessLocale('change_password');
                                onClose();
                            })
                            .catch((e) => {
                                requestErrorLocale(e);
                                changeModalData({ ...data, code: '' });
                            })
                            .finally(() => handleChangeLoading(false));
                    }
                });
            }
        });
    };

    const handleOpenAddPhoneModal = (refetchPhones: () => void) => {
        onOpen({
            modalId: modalIds.SETTINGS_ADD_PHONE_MODAL,
            onSubmit: (data: TNewPhone) => {
                handleChangeLoading(true);
                apiUserService
                    .addPhone(data)
                    .then(({ id }) => {
                        refetchPhones();
                        changeModalData({ ...data, phoneId: id });
                        onOpen({
                            modalId: modalIds.SETTINGS_VERIFICATION_PHONE,
                            onSubmit: (data: { phoneId: string; code: string }) => {
                                handleChangeLoading(true);
                                apiUserService
                                    .verifyPhone(data.phoneId, data?.code as string)
                                    .then(() => {
                                        requestSuccessLocale('verify_phone');
                                        onClose();
                                        refetchPhones();
                                    })
                                    .catch((e) => {
                                        requestErrorLocale(e);
                                        changeModalData({ ...data, code: '' });
                                    })
                                    .finally(() => handleChangeLoading(false));
                            }
                        });
                    })
                    .catch(requestErrorLocale)
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleOpenAddEmailModal = (refetchEmails: () => void) => {
        onOpen({
            modalId: modalIds.SETTINGS_ADD_EMAIL_MODAL,
            onSubmit: (data: { email: string }) => {
                if (data?.email) {
                    handleChangeLoading(true);
                    apiUserService
                        .addEmail(data.email as string)
                        .then(({ id }) => {
                            refetchEmails();
                            changeModalData({ ...data, emailId: id, is_primary: false });
                            requestSuccessLocale('send_verification_email');
                            onOpen({
                                modalId: modalIds.SETTINGS_VERIFICATION_EMAIL,
                                onSubmit: (data: { emailId: string; code: string }) => {
                                    handleChangeLoading(true);
                                    apiUserService
                                        .verifyEMAIL(data.emailId, data?.code as string)
                                        .then(() => {
                                            requestSuccessLocale('verify_email');
                                            onClose();
                                            refetchEmails();
                                        })
                                        .catch((e) => {
                                            requestErrorLocale(e);
                                            changeModalData({ ...data, code: '' });
                                        })
                                        .finally(() => handleChangeLoading(false));
                                }
                            });
                        })
                        .catch(requestErrorLocale)
                        .finally(() => handleChangeLoading(false));
                }
            }
        });
    };

    const handleSetPrimaryEmail = (email: TEmail, refetchEmails: () => void) => {
        changeModalData({
            email: email.email,
            emailId: email.id,
            is_primary: true,
            is_primary_set: true
        });
        onOpen({
            modalId: modalIds.SETTINGS_VERIFICATION_EMAIL,
            onSubmit: (data: { secure_id: string; code: string }) => {
                handleChangeLoading(true);
                apiUserService
                    .setPrimaryEmail(email.id, {
                        id: data?.secure_id,
                        code: data?.code
                    })
                    .then(() => {
                        requestSuccessLocale('set_primary_email');
                        onClose();
                        refetchEmails();
                    })
                    .catch((e) => {
                        requestErrorLocale(e);
                        changeModalData({ ...data, code: '' });
                    })
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleSetPrimaryPhone = (phone: TPhone, refetchPhones: () => void) => {
        onOpen({
            modalId: modalIds.USER_VERIFICATION_PHONE,
            onSubmit: (data: { secure_id: string; code: string }) => {
                handleChangeLoading(true);
                apiUserService
                    .setPrimaryPhone(phone.id, {
                        id: data?.secure_id,
                        code: data?.code
                    })
                    .then(() => {
                        requestSuccessLocale('set_primary_phone');
                        onClose();
                        refetchPhones();
                    })
                    .catch((e) => {
                        requestErrorLocale(e);
                        changeModalData({ ...data, code: '' });
                    })
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleVerifyPhone = (phone: TPhone, refetchPhones: () => void) => {
        changeModalData({
            phoneId: phone.id,
            phone_number: phone.phone_number,
            phone_code: phone.phone_code
        });
        onOpen({
            modalId: modalIds.SETTINGS_VERIFICATION_PHONE,
            onSubmit: (data: { phoneId: string; code: string }) => {
                handleChangeLoading(true);
                apiUserService
                    .verifyPhone(data.phoneId, data?.code as string)
                    .then(() => {
                        requestSuccessLocale('verify_phone');
                        onClose();
                        refetchPhones();
                    })
                    .catch((e) => {
                        requestErrorLocale(e);
                        changeModalData({ ...data, code: '' });
                    })
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleVerifyEmail = (email: TEmail, refetchEmails: () => void) => {
        changeModalData({ ...email, emailId: email.id, isPrimary: false });
        onOpen({
            modalId: modalIds.SETTINGS_VERIFICATION_EMAIL,
            onSubmit: (data: { emailId: string; code: string }) => {
                handleChangeLoading(true);
                apiUserService
                    .verifyEMAIL(data.emailId, data?.code as string)
                    .then(() => {
                        requestSuccessLocale('verify_email');
                        onClose();
                        refetchEmails();
                    })
                    .catch((e) => {
                        requestErrorLocale(e);
                        changeModalData({ ...data, code: '' });
                    })
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleDeletePhone = (data: TPhone, refetchPhones: () => void) => {
        changeModalData({
            phone_code: data.phone_code,
            phone_number: data.phone_number
        });
        onOpen({
            modalId: modalIds.DELETE_PHONE,
            onSubmit: () => {
                handleChangeLoading(true);
                apiUserService
                    .deletePhone(data.id)
                    .then(() => {
                        requestSuccessLocale('delete_phone');
                        onClose();
                        refetchPhones();
                    })
                    .catch(requestErrorLocale)
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleDeleteEmail = (data: TEmail, refetchEmails: () => void) => {
        changeModalData({ email: data.email });
        onOpen({
            modalId: modalIds.DELETE_EMAIL,
            onSubmit: () => {
                handleChangeLoading(true);
                apiUserService
                    .deleteEmail(data.id)
                    .then(() => {
                        requestSuccessLocale('delete_email');
                        onClose();
                        refetchEmails();
                    })
                    .catch(requestErrorLocale)
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleChangePassword = (
        oldPassword: string,
        newPassword: string,
        onSuccess: () => void
    ) => {
        onOpen({
            modalId: modalIds.USER_VERIFICATION_PHONE,
            onSubmit: (data: { secure_id: string; code: string }) => {
                handleChangeLoading(true);
                apiUserService
                    .changePassword(oldPassword, newPassword, {
                        id: data?.secure_id,
                        code: data?.code
                    })
                    .then(() => {
                        requestSuccessLocale('password_changed');
                        onSuccess();
                        onClose();
                        refetchUser();
                    })
                    .catch((e) => {
                        requestErrorLocale(e);
                        changeModalData({ ...data, code: '' });
                    })
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleEnableMFA = (type: ETwoFAMethod, onSubmit: () => void) => {
        changeModalData({
            type
        });
        onOpen({
            modalId: modalIds.ENABLE_MFA,
            onSubmit
        });
    };

    const handleConfirmDisableMFA = (type: ETwoFAMethod, token: string) => {
        changeModalData({
            type,
            isCodeSent: true
        });
        onOpen({
            modalId: modalIds.DISABLE_MFA,
            onSubmit: (data: { mfaCode: string; isRecoveryCode: boolean }) => {
                if (!data?.isRecoveryCode) {
                    handleVerifyMFA({ token, code: data?.mfaCode });
                } else {
                    handleResetOTPRequest(data?.mfaCode);
                }
            }
        });
    };

    const handleDisableMFA = (type: ETwoFAMethod) => {
        changeModalData({
            type
        });
        onOpen({
            modalId: modalIds.DISABLE_MFA,
            onSubmit: () => {
                handleDisableMFARequest(type);
            }
        });
    };

    const handleSetPhoneMFA = () => {
        onClose();
        apiAuthService
            .setupTwoFA(ETwoFAMethod.SMS)
            .then((mfaResult) => {
                const tokenRef = { current: mfaResult.token };
                changeModalData({
                    phone_number: user?.phone_number,
                    phone_code: user?.phone_code,
                    customCode: true,
                    is_primary: true
                });
                onOpen({
                    modalId: modalIds.SETTINGS_VERIFICATION_PHONE,
                    onSubmit: (data: { phoneId: string; code: string }) => {
                        handleChangeLoading(true);
                        apiAuthService
                            .confirmTwoFA({ token: tokenRef.current, code: data?.code as string })
                            .then((result) => {
                                if (result) setAuthenticatorCode(result);
                                refetchUser();
                                onClose();
                                handleChangeLoading(false);
                            })
                            .catch((e) => {
                                requestErrorLocale(e);
                                // Keep loading until the refreshed token is ready so a fast
                                // second submit cannot race with the stale tokenRef.current.
                                apiAuthService
                                    .setupTwoFA(ETwoFAMethod.SMS)
                                    .then((r) => { tokenRef.current = r.token; })
                                    .catch(requestErrorLocale)
                                    .finally(() => handleChangeLoading(false));
                            });
                    }
                });
            })
            .catch(requestErrorLocale);
    };

    const handleSetEmailMFA = () => {
        onClose();
        apiAuthService
            .setupTwoFA(ETwoFAMethod.EMAIL)
            .then((mfaResult) => {
                const tokenRef = { current: mfaResult.token };
                changeModalData({
                    email: user?.email,
                    customCode: true,
                    is_primary: true
                });
                onOpen({
                    modalId: modalIds.SETTINGS_VERIFICATION_EMAIL,
                    onSubmit: (data: { phoneId: string; code: string }) => {
                        handleChangeLoading(true);
                        apiAuthService
                            .confirmTwoFA({ token: tokenRef.current, code: data?.code as string })
                            .then((result) => {
                                if (result) setAuthenticatorCode(result);
                                refetchUser();
                                onClose();
                                handleChangeLoading(false);
                            })
                            .catch((e) => {
                                requestErrorLocale(e);
                                // Keep loading until the refreshed token is ready so a fast
                                // second submit cannot race with the stale tokenRef.current.
                                apiAuthService
                                    .setupTwoFA(ETwoFAMethod.EMAIL)
                                    .then((r) => { tokenRef.current = r.token; })
                                    .catch(requestErrorLocale)
                                    .finally(() => handleChangeLoading(false));
                            });
                    }
                });
            })
            .catch(requestErrorLocale);
    };

    const handleDeleteApiKey = (onSuccess: () => void, environment?: string) => {
        onOpen({
            modalId: modalIds.DELETE_API_KEYS,
            onSubmit: () => {
                handleChangeLoading(true);
                // Env-scoped: deleting the test key must not revoke the live key.
                // Omitting environment (legacy settings page) revokes every env.
                apiKeyService
                    .remove(environment)
                    .then(() => {
                        requestSuccessLocale('delete_api_key');
                        onClose();
                        onSuccess();
                    })
                    .catch(requestErrorLocale)
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleCreateApiKey = (
        onSuccess: () => void,
        apiKeys?: { access_key?: string; scopes?: string[] },
        environment?: string
    ) => {
        const runCreate = (scopes?: string[]) => {
            handleChangeLoading(true);
            apiKeyService
                .create(scopes && scopes.length ? scopes : [SCOPE_FULL_ACCESS], environment)
                .then((data) => {
                    requestSuccessLocale('create_api_key');
                    onSuccess();
                    changeModalData(data);
                    onOpen({
                        modalId: modalIds.CREATE_API_KEYS,
                        onSubmit: onClose
                    });
                })
                .catch(requestErrorLocale)
                .finally(() => handleChangeLoading(false));
        };

        // Both create and regenerate go through the permission picker so the
        // merchant chooses the key's read/write level up front. On regenerate,
        // default the picker to the CURRENT key's level so a read-only key is
        // never silently widened to full access by a blind confirm (the old flow
        // always regenerated to full access). The config modal seeds
        // modalData.scopes; its footer confirm passes that back to onSubmit.
        const currentScopes = apiKeys?.scopes;
        const currentLevel =
            apiKeys?.access_key && describeScopes(currentScopes) === 'Read only'
                ? 'read_only'
                : 'full';
        changeModalData({
            isRegenerate: !!apiKeys?.access_key,
            level: currentLevel,
            scopes: scopesForLevel(currentLevel)
        });
        onOpen({
            modalId: modalIds.CREATE_API_KEY_CONFIG,
            onSubmit: (data?: { scopes?: string[] }) => runCreate(data?.scopes)
        });
    };

    return {
        handleChangeAvatar,
        handleDeleteAvatar,
        handleChangeLogo,
        handleOpenChangePasswordModal,
        handleOpenAddPhoneModal,
        handleOpenAddEmailModal,
        handleSetPrimaryPhone,
        handleDeletePhone,
        handleSetPrimaryEmail,
        handleDeleteEmail,
        handleVerifyPhone,
        handleVerifyEmail,
        handleChangePassword,
        handleSetEmailMFA,
        handleSetPhoneMFA,
        handleEnableMFA,
        authenticatorCode,
        handleDisableMFA,
        handleCreateApiKey,
        handleDeleteApiKey
    };
};
